import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewLeadModal = ({ onClose, onCreate }) => {
  const [regions, setRegions] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', region: 'default', message: '', from_email: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/leads/regions`, { withCredentials: true })
      .then((res) => setRegions(res.data.regions || [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/admin/email-identities`, { withCredentials: true })
      .then((res) => setIdentities(res.data.identities || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim() || saving) return;
    setSaving(true);
    const payload = { name: form.name.trim(), email: form.email.trim(), region: form.region, message: form.message.trim() };
    if (form.from_email) payload.from_email = form.from_email;
    const ok = await onCreate(payload);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="new-lead-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">New Lead</h3>
          <button type="button" onClick={onClose} data-testid="new-lead-modal-close">
            <X className="w-5 h-5 text-slate-400 hover:text-slate-700" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="new-lead-name-input"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="new-lead-email-input"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Region</label>
            <select
              value={form.region}
              onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
              data-testid="new-lead-region-select"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              {regions.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Send from (optional — defaults by region)</label>
            <select
              value={form.from_email}
              onChange={(e) => setForm((f) => ({ ...f, from_email: e.target.value }))}
              data-testid="new-lead-from-select"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Auto (region default)</option>
              {identities.map((i) => <option key={i.id} value={i.email}>{i.label} — {i.email}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">First message</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              rows={4}
              data-testid="new-lead-message-input"
              className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !form.name.trim() || !form.email.trim() || !form.message.trim()}
            data-testid="new-lead-submit-button"
            className="w-full bg-slate-900 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Creating…' : 'Create Lead & Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewLeadModal;
